import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Building2,
  Bell,
  ShieldCheck,
  SlidersHorizontal,
  KeyRound,
  Mail,
  Globe,
  Moon,
  Save,
  Loader2,
  ExternalLink,
  TriangleAlert,
  RefreshCw,
  CheckCircle2,
  Briefcase,
  MapPin,
  Receipt,
} from 'lucide-react';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import {
  changeRecruiterPassword,
  getRecruiterSettings,
  updateRecruiterSettings,
} from '../Services/RecruiterSettingsService';
import {
  LANGUAGE_OPTIONS,
  RecruiterSettings,
  RecruiterSettingsUpdate,
} from '../Types/RecruiterSettingsType';
import { getPasswordValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import { PasswordPolicyHints } from '../../Shared/Component/forms/PasswordPolicyHints';

type TabId = 'account' | 'company' | 'notifications' | 'security' | 'preferences';

const TABS: { id: TabId; label: string; icon: React.ComponentType<any> }[] = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: ShieldCheck },
  { id: 'preferences', label: 'Preferences', icon: SlidersHorizontal },
];

const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-cyan-500/50 disabled:cursor-not-allowed disabled:opacity-60';

const display = (value: string | null | undefined, fallback = 'Not provided') =>
  value && value.trim().length > 0 ? value : fallback;

/**
 * Controlled toggle. State lives in the page so a change is only persisted when the
 * section is saved, which matches how the rest of the console treats preferences.
 */
const ToggleRow = ({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) => (
  <div
    className={`flex items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-3.5 transition-colors ${
      disabled ? 'opacity-60' : 'hover:border-cyan-500/20'
    }`}
  >
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-200">{label}</p>
      <p className="text-[11px] font-medium text-slate-500">{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
        checked ? 'bg-cyan-500' : 'bg-slate-700'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const SectionHeader = ({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) => (
  <div className="mb-5 flex items-start gap-3 border-b border-slate-800/80 pb-4">
    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
      {icon}
    </span>
    <div>
      <h2 className="text-base font-black tracking-tight text-slate-100">{title}</h2>
      <p className="text-[11px] font-semibold text-slate-500">{subtitle}</p>
    </div>
  </div>
);

const ReadOnlyRow = ({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) => (
  <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-3.5">
    <div className="flex min-w-0 items-center gap-3">
      <span className="shrink-0 text-slate-500">{icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-200">{label}</p>
        <p className="truncate text-[11px] font-medium text-slate-500">
          {hint ?? 'Read only on this screen'}
        </p>
      </div>
    </div>
    <span className="shrink-0 text-right text-xs font-bold text-slate-300">{value}</span>
  </div>
);

const PlaceholderNote = ({ label }: { label: string }) => (
  <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-700 bg-slate-950/40 px-4 py-3 text-[11px] font-semibold text-slate-500">
    <TriangleAlert size={13} className="shrink-0 text-amber-400" />
    {label}
  </div>
);

const SaveBar = ({
  dirty,
  saving,
  onSave,
  label,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  label: string;
}) => (
  <div className="flex flex-wrap items-center justify-end gap-3">
    {dirty && !saving && (
      <span className="text-[11px] font-bold text-amber-400">You have unsaved changes</span>
    )}
    <button
      type="button"
      onClick={onSave}
      disabled={saving || !dirty}
      className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
      {label}
    </button>
  </div>
);

function RecruiterSettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('account');
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [settings, setSettings] = useState<RecruiterSettings | null>(null);
  const [draft, setDraft] = useState<RecruiterSettings | null>(null);
  const [savingSection, setSavingSection] = useState<TabId | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const { policy } = useValidationPolicy();

  const redirect = useRedirect();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const response = await getRecruiterSettings();

    if (response.success && response.data) {
      setSettings(response.data);
      setDraft(response.data);
      setLoadError(null);
    } else {
      setSettings(null);
      setDraft(null);
      setLoadError(response.message || 'Could not load your settings.');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const notificationDirty = useMemo(() => {
    if (!settings || !draft) return false;
    return (
      settings.emailNotifications !== draft.emailNotifications ||
      settings.pushNotifications !== draft.pushNotifications ||
      settings.productAnnouncements !== draft.productAnnouncements
    );
  }, [settings, draft]);

  const preferenceDirty = useMemo(() => {
    if (!settings || !draft) return false;
    return (
      settings.darkTheme !== draft.darkTheme ||
      settings.compactTables !== draft.compactTables ||
      settings.autoplayMedia !== draft.autoplayMedia ||
      settings.language !== draft.language
    );
  }, [settings, draft]);

  const patchDraft = (patch: RecruiterSettingsUpdate) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const save = async (payload: RecruiterSettingsUpdate, section: TabId, label: string) => {
    setSavingSection(section);
    setAlert(null);

    const response = await updateRecruiterSettings(payload);

    if (response.success && response.data) {
      // Trust the server response so defaults and validation stay authoritative.
      setSettings(response.data);
      setDraft(response.data);
      setAlert({
        variant: 'success',
        title: 'Saved',
        message: label,
        autoCloseMs: 4000,
        onClose: () => setAlert(null),
      });
    } else {
      setAlert({
        variant: 'error',
        title: 'Could not save',
        message: response.message || 'Something went wrong. Your changes were not saved.',
        onClose: () => setAlert(null),
      });
      // Re-sync so the toggles do not keep showing a value the server rejected.
      if (settings) setDraft(settings);
    }

    setSavingSection(null);
  };

  const saveNotifications = () => {
    if (!draft) return;
    save(
      {
        emailNotifications: draft.emailNotifications,
        pushNotifications: draft.pushNotifications,
        productAnnouncements: draft.productAnnouncements,
      },
      'notifications',
      'Your notification preferences have been updated.'
    );
  };

  const savePreferences = () => {
    if (!draft) return;
    save(
      {
        darkTheme: draft.darkTheme,
        compactTables: draft.compactTables,
        autoplayMedia: draft.autoplayMedia,
        language: draft.language,
      },
      'preferences',
      'Your console preferences have been updated.'
    );
  };

  const handleChangePassword = async () => {
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Fill in all three fields.');
      return;
    }
    const policyError = getPasswordValidationError(newPassword, policy);
    if (policyError) {
      setPasswordError(policyError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('The new passwords do not match.');
      return;
    }

    setPasswordBusy(true);
    const response = await changeRecruiterPassword({ currentPassword, newPassword });

    if (response.success) {
      const revoked = response.data?.otherSessionsRevoked ?? 0;
      setAlert({
        variant: 'success',
        title: 'Password updated',
        message: `${revoked} active ${
          revoked === 1 ? 'session was' : 'sessions were'
        } signed out, including this one. Please sign in again.`,
        autoCloseMs: 8000,
        onClose: () => setAlert(null),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Every refresh token was just revoked, so the active session count is stale.
      load();
    } else {
      setPasswordError(response.message || 'Could not update your password.');
    }

    setPasswordBusy(false);
  };

  const fullName = useMemo(() => {
    if (!settings) return '';
    return [settings.firstName, settings.lastName].filter(Boolean).join(' ').trim();
  }, [settings]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-100">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={26} className="animate-spin text-cyan-400" />
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">
            Loading settings
          </p>
        </div>
      </div>
    );
  }

  if (loadError || !settings || !draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6 text-slate-100">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-red-500/30 bg-slate-900/60 p-6 text-center">
          <TriangleAlert size={26} className="mx-auto text-red-400" />
          <h2 className="text-base font-black tracking-tight">Settings unavailable</h2>
          <p className="text-xs font-medium text-slate-400">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="mx-auto flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
          >
            <RefreshCw size={14} /> Try again
          </button>
        </div>
      </div>
    );
  }

  const savingNotifications = savingSection === 'notifications';
  const savingPreferences = savingSection === 'preferences';

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        {alert && <Alert {...alert} />}

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
          <div className="absolute left-0 right-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-70" />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                <SlidersHorizontal size={20} />
              </span>
              <div>
                <p className="text-[11px] font-black uppercase tracking-widest text-cyan-400">
                  Recruiter Console
                </p>
                <h1 className="text-lg font-black tracking-tight text-slate-100 sm:text-2xl">
                  Account Settings
                </h1>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-slate-200">{display(fullName, 'Recruiter')}</p>
              <p className="text-[11px] font-medium text-slate-500">
                {display(settings.email)}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40 p-1.5 no-scrollbar">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest transition-all ${
                  isActive
                    ? 'bg-cyan-500 text-slate-950 shadow-lg shadow-cyan-950/40'
                    : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
                }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6"
        >
          {activeTab === 'account' && (
            <div>
              <SectionHeader
                icon={<User size={18} />}
                title="Account"
                subtitle="Your recruiter identity, as held on the recruiter record"
              />
              <div className="space-y-3">
                <ReadOnlyRow
                  icon={<Mail size={16} />}
                  label="Email Address"
                  value={display(settings.email)}
                />
                <ReadOnlyRow
                  icon={<User size={16} />}
                  label="Full Name"
                  value={display(fullName)}
                />
                <ReadOnlyRow
                  icon={<Briefcase size={16} />}
                  label="Designation"
                  value={display(settings.designation)}
                />
                <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-950/50 px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <ShieldCheck size={16} className="shrink-0 text-slate-500" />
                    <div>
                      <p className="text-sm font-semibold text-slate-200">Console Access</p>
                      <p className="text-[11px] font-medium text-slate-500">
                        {settings.companyAdminUser
                          ? 'You administer this company workspace'
                          : 'You are a member of this company workspace'}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${
                      settings.recruiterStatus
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                        : 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                    }`}
                  >
                    {settings.recruiterStatus ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <PlaceholderNote label="Name, designation and email are edited on your profile page." />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => redirect('/recruiter/profile')}
                    className="flex items-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:bg-cyan-500/20"
                  >
                    <ExternalLink size={14} /> Go to Profile
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'company' && (
            <div>
              <SectionHeader
                icon={<Building2 size={18} />}
                title="Company"
                subtitle="The company your recruiter account is attached to"
              />
              <div className="space-y-3">
                {settings.companyId ? (
                  <>
                    <ReadOnlyRow
                      icon={<Building2 size={16} />}
                      label="Company"
                      value={display(settings.companyName)}
                    />
                    <ReadOnlyRow
                      icon={<Globe size={16} />}
                      label="Company Domain"
                      value={display(settings.companyDomain)}
                    />
                    <ReadOnlyRow
                      icon={<Briefcase size={16} />}
                      label="Industry"
                      value={display(settings.industry)}
                    />
                    <ReadOnlyRow
                      icon={<MapPin size={16} />}
                      label="Headquarters"
                      value={display(settings.headquartersLocation)}
                    />
                    <ReadOnlyRow
                      icon={<Receipt size={16} />}
                      label="GST Number"
                      value={display(settings.gstNumber)}
                    />
                    <ReadOnlyRow
                      icon={<CheckCircle2 size={16} />}
                      label="Subscription"
                      value={
                        settings.subscriptionPlanName
                          ? `${settings.subscriptionPlanName}${
                              settings.subscriptionPlanStatus
                                ? ` (${settings.subscriptionPlanStatus})`
                                : ''
                            }`
                          : 'No active plan'
                      }
                    />
                  </>
                ) : (
                  <PlaceholderNote label="No company is linked to your recruiter account yet." />
                )}
                <PlaceholderNote label="Company name, logo, GST and headquarters are edited on the profile page by a company admin." />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => redirect('/recruiter/profile')}
                    className="flex items-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:bg-cyan-500/20"
                  >
                    <ExternalLink size={14} /> Manage Company
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div>
              <SectionHeader
                icon={<Bell size={18} />}
                title="Notifications"
                subtitle="Choose how Nexgent keeps you updated"
              />
              <div className="space-y-3">
                <ToggleRow
                  label="Email Notifications"
                  description="Receive digests for new invitations and candidate activity"
                  checked={draft.emailNotifications}
                  disabled={savingNotifications}
                  onChange={(next) => patchDraft({ emailNotifications: next })}
                />
                <ToggleRow
                  label="Push Notifications"
                  description="Real-time alerts inside the recruiter console"
                  checked={draft.pushNotifications}
                  disabled={savingNotifications}
                  onChange={(next) => patchDraft({ pushNotifications: next })}
                />
                <ToggleRow
                  label="Product Announcements"
                  description="News about new features and improvements"
                  checked={draft.productAnnouncements}
                  disabled={savingNotifications}
                  onChange={(next) => patchDraft({ productAnnouncements: next })}
                />
                <SaveBar
                  dirty={notificationDirty}
                  saving={savingNotifications}
                  onSave={saveNotifications}
                  label="Save Preferences"
                />
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div>
              <SectionHeader
                icon={<ShieldCheck size={18} />}
                title="Security"
                subtitle="Protect access to your recruiter account"
              />
              <div className="space-y-3">
                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <KeyRound size={16} className="shrink-0 text-slate-500" />
                    <div>
                      <p className="text-sm font-semibold text-slate-200">Change Password</p>
                      <p className="text-[11px] font-medium text-slate-500">
                        Signing out every active session, including this one
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="block md:col-span-2">
                      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        Current Password
                      </span>
                      <input
                        type="password"
                        autoComplete="current-password"
                        className={inputClass}
                        value={currentPassword}
                        disabled={passwordBusy}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        New Password
                      </span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        className={inputClass}
                        value={newPassword}
                        disabled={passwordBusy}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        Confirm New Password
                      </span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        className={inputClass}
                        value={confirmPassword}
                        disabled={passwordBusy}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </label>
                  </div>
                  <PasswordPolicyHints password={newPassword} policy={policy} className="mt-3" />
                  {passwordError && (
                    <p className="mt-3 text-[11px] font-bold text-red-400">{passwordError}</p>
                  )}
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleChangePassword}
                      disabled={passwordBusy}
                      className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {passwordBusy ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <KeyRound size={15} />
                      )}
                      Update Password
                    </button>
                  </div>
                </div>
                <ReadOnlyRow
                  icon={<Globe size={16} />}
                  label="Active Sessions"
                  value={`${settings.activeSessions} ${
                    settings.activeSessions === 1 ? 'device' : 'devices'
                  }`}
                  hint="Devices that can still mint a new access token"
                />
                <PlaceholderNote label="Two-factor authentication is not available on this account type." />
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div>
              <SectionHeader
                icon={<SlidersHorizontal size={18} />}
                title="Preferences"
                subtitle="Personalise the recruiter console experience"
              />
              <div className="space-y-3">
                <ToggleRow
                  label="Dark Console Theme"
                  description="Use the dark recruiter workspace"
                  checked={draft.darkTheme}
                  disabled={savingPreferences}
                  onChange={(next) => patchDraft({ darkTheme: next })}
                />
                <ToggleRow
                  label="Compact Tables"
                  description="Show denser rows in listing tables"
                  checked={draft.compactTables}
                  disabled={savingPreferences}
                  onChange={(next) => patchDraft({ compactTables: next })}
                />
                <ToggleRow
                  label="Autoplay Media"
                  description="Preview candidate media automatically"
                  checked={draft.autoplayMedia}
                  disabled={savingPreferences}
                  onChange={(next) => patchDraft({ autoplayMedia: next })}
                />
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400">
                    Language
                  </span>
                  <select
                    className={inputClass}
                    value={draft.language}
                    disabled={savingPreferences}
                    onChange={(e) => patchDraft({ language: e.target.value })}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <SaveBar
                  dirty={preferenceDirty}
                  saving={savingPreferences}
                  onSave={savePreferences}
                  label="Save Preferences"
                />
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default RecruiterSettingsPage;
